import { useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { lazy, Suspense } from "react";
import { AiLoader } from "@/components/AiLoader";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import { CookieConsentBanner } from "@/components/legal/CookieConsentBanner";
import { CartProvider } from "@/contexts/CartContext";
import { PolicyConsentGate } from "@/components/legal/PolicyConsentGate";
import { FeedbackDialog } from "@/components/feedback/FeedbackDialog";

// Eager-load critical layout
const DashboardLayout = lazy(() => import("./components/DashboardLayout"));
const DashboardHomePage = lazy(() => import("./pages/DashboardHomePage"));

// Preload layout immediately for faster first navigation
import("./components/DashboardLayout");

// Preload common pages after idle
if (typeof window !== "undefined" && "requestIdleCallback" in window) {
  (window as any).requestIdleCallback(() => {
    import("./pages/DashboardHomePage");
    import("./pages/LeadsPage");
    import("./pages/CommerceOrdersPage");
  });
}
// Lazy-loaded pages
const DiscoveryPage = lazy(() => import("./pages/DiscoveryPage"));

const CreativeStudioPage = lazy(() => import("./pages/CreativeStudioPage"));
const FinancialPage = lazy(() => import("./pages/FinancialPage"));
const AdsPage = lazy(() => import("./pages/AdsPage"));
const InventoryPage = lazy(() => import("./pages/InventoryPage"));
const PerformancePage = lazy(() => import("./pages/PerformancePage"));
const ReorderPage = lazy(() => import("./pages/ReorderPage"));
const GlobalStatisticsPage = lazy(() => import("./pages/GlobalStatisticsPage"));
const ProfitSimulatorPage = lazy(() => import("./pages/ProfitSimulatorPage"));

const AiToolsPage = lazy(() => import("./pages/AiToolsPage"));
const StoreConnectionsPage = lazy(() => import("./pages/StoreConnectionsPage"));
const LeadsPage = lazy(() => import("./pages/LeadsPage"));
const AgentSettingsPage = lazy(() => import("./pages/AgentSettingsPage"));
const UserSettingsPage = lazy(() => import("./pages/UserSettingsPage"));

const CommerceOrdersPage = lazy(() => import("./pages/CommerceOrdersPage"));
const ShopifyStorePage = lazy(() => import("./pages/ShopifyStorePage"));
const ShopifyProductPage = lazy(() => import("./pages/ShopifyProductPage"));


const Team = lazy(() => import("./pages/Team"));
const Pricing = lazy(() => import("./pages/Pricing"));
const AffiliateDashboard = lazy(() => import("./pages/AffiliateDashboard"));
const AdminCommandCenter = lazy(() => import("./pages/AdminCommandCenter"));
const DataExportPage = lazy(() => import("./pages/DataExportPage"));

const Settings = lazy(() => import("./pages/Settings"));
const AiLearningImportPage = lazy(() => import("./pages/AiLearningImportPage"));

const FulfillmentIntelligencePage = lazy(() => import("./pages/FulfillmentIntelligencePage"));
// QuickLaunch now redirects to Creative Studio
const ContinuousLearningPage = lazy(() => import("./pages/ContinuousLearningPage"));
const AiEvaluationPage = lazy(() => import("./pages/AiEvaluationPage"));
const AiActionsPage = lazy(() => import("./pages/AiActionsPage"));
const OperationalTimelinePage = lazy(() => import("./pages/OperationalTimelinePage"));
const BenchmarkPage = lazy(() => import("./pages/BenchmarkPage"));
const ExperimentationPage = lazy(() => import("./pages/ExperimentationPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <AiLoader text="Loading…" />
    </div>
  );
}

function ProtectedRoute({ children, allowExpired = false, ownerOnly = false, agentOnly = false, agentAllowed = false }: { children: React.ReactNode; allowExpired?: boolean; ownerOnly?: boolean; agentOnly?: boolean; agentAllowed?: boolean }) {
  const { user, loading, isTrialActive, subscriptionPlan, effectiveRole, accountStatus, impersonation } = useAuth();
  const role = effectiveRole;

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  // Role may still be loading after sign-in; show loader instead of redirecting to avoid loop
  if (!role) return <PageLoader />;

  // Owner-only route guard
  if (ownerOnly && role !== "owner") return <Navigate to="/" replace />;

  // Agent-only route guard (owner can also access)
  if (agentOnly && role !== "agent" && role !== "owner") return <Navigate to="/dashboard" replace />;

  // Agents can now access most routes (blocked only from ownerOnly routes)

  // Block pending/rejected (owners bypass)
  if (role !== "owner" && accountStatus && accountStatus !== "approved") return <Navigate to="/" replace />;

  // Super Admin always bypasses trial/subscription
  if (role === "owner") return <>{children}</>;

  // During impersonation, bypass trial/subscription checks (owner is still behind the scenes)
  if (impersonation.active) return <>{children}</>;

  // Agent and User: must have active trial or subscription
  // But let pending/unapproved users through so DashboardLayout can show the pending screen
  if (!allowExpired && !isTrialActive && !subscriptionPlan && accountStatus === "approved") return <Navigate to="/pricing" replace />;

  return <>{children}</>;
}

function HomeRoute() {
  const { user, loading, effectiveRole } = useAuth();
  const role = effectiveRole;
  if (loading) return <PageLoader />;
  if (!user) return <Auth />;
  if (role === "agent") return <Navigate to="/dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppContent() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public legal pages */}
            <Route path="/legal/:type" element={<LegalPage />} />

            {/* Auth */}
            <Route path="/" element={<HomeRoute />} />
            <Route path="/auth" element={<HomeRoute />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Single shared DashboardLayout for all dashboard routes */}
            <Route element={<ProtectedRoute><PolicyConsentGate><DashboardLayout /></PolicyConsentGate></ProtectedRoute>}>
              <Route path="/dashboard" element={<DashboardHomePage />} />
              <Route path="/discovery" element={<DiscoveryPage />} />
              <Route path="/creative-studio" element={<CreativeStudioPage />} />
              <Route path="/financial" element={<FinancialPage />} />
              <Route path="/ads" element={<AdsPage />} />
              <Route path="/inventory" element={<InventoryPage />} />
              <Route path="/performance" element={<PerformancePage />} />
              <Route path="/reorder" element={<ReorderPage />} />
              <Route path="/global-statistics" element={<GlobalStatisticsPage />} />
              <Route path="/profit-simulator" element={<ProfitSimulatorPage />} />
              
              
              <Route path="/store-connections" element={<StoreConnectionsPage />} />
              <Route path="/store" element={<ShopifyStorePage />} />
              <Route path="/product/:handle" element={<ShopifyProductPage />} />
              <Route path="/leads" element={<LeadsPage />} />
              
              <Route path="/commerce-orders" element={<CommerceOrdersPage />} />
              
              <Route path="/shipping" element={<Navigate to="/fulfillment" replace />} />
              <Route path="/ai-tools" element={<AiToolsPage />} />
              
              <Route path="/user-settings" element={<UserSettingsPage />} />
              <Route path="/generation-history" element={<Navigate to="/dashboard" replace />} />
              
              <Route path="/fulfillment" element={<FulfillmentIntelligencePage />} />
              <Route path="/pipeline" element={<Navigate to="/fulfillment" replace />} />
              
              <Route path="/offer-lab" element={<Navigate to="/ai-tools" replace />} />
              <Route path="/growth-tools" element={<Navigate to="/ai-tools" replace />} />
              <Route path="/quick-launch" element={<Navigate to="/creative-studio" replace />} />
              <Route path="/decisions" element={<Navigate to="/ai-tools" replace />} />
              <Route path="/automation" element={<Navigate to="/ai-tools" replace />} />
              <Route path="/operational-timeline" element={<OperationalTimelinePage />} />
              <Route path="/benchmarks" element={<BenchmarkPage />} />
              <Route path="/experiments" element={<ExperimentationPage />} />
            </Route>

            {/* Owner-only routes */}
            <Route element={<ProtectedRoute ownerOnly><DashboardLayout /></ProtectedRoute>}>
              <Route path="/team" element={<Team />} />
              <Route path="/ai-learning" element={<AiLearningImportPage />} />
              <Route path="/continuous-learning" element={<ContinuousLearningPage />} />
              <Route path="/ai-evaluation" element={<AiEvaluationPage />} />
              <Route path="/ai-actions" element={<AiActionsPage />} />
            </Route>

            {/* Owner + Agent routes */}
            <Route element={<ProtectedRoute agentOnly><DashboardLayout /></ProtectedRoute>}>
              <Route path="/command-center" element={<AdminCommandCenter />} />
            </Route>

            {/* Data export - all roles */}
            <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route path="/data-export" element={<DataExportPage />} />
            </Route>

            {/* Owner-only routes */}
            <Route element={<ProtectedRoute ownerOnly><DashboardLayout /></ProtectedRoute>}>
              <Route path="/settings" element={<Settings />} />
            </Route>

            {/* Agent-only routes */}
            <Route element={<ProtectedRoute agentOnly><DashboardLayout /></ProtectedRoute>}>
              <Route path="/affiliate" element={<AffiliateDashboard />} />
            </Route>

            {/* Agent-allowed routes */}
            <Route element={<ProtectedRoute agentAllowed><DashboardLayout /></ProtectedRoute>}>
              <Route path="/agent-settings" element={<AgentSettingsPage />} />
            </Route>


            {/* Legacy redirects */}
            <Route path="/statistics" element={<Navigate to="/global-statistics" replace />} />
            <Route path="/analytics" element={<Navigate to="/global-statistics" replace />} />
            <Route path="/product-research" element={<Navigate to="/discovery" replace />} />

            {/* Standalone */}
            <Route path="/pricing" element={<ProtectedRoute allowExpired><Pricing /></ProtectedRoute>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <ScrollToTop />
        <CookieConsentBanner />
        <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
        </CartProvider>
        <footer className="w-full py-4 border-t border-border/30">
          <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-2">
            <span className="text-[11px] text-muted-foreground/80">
              Powered by <span className="font-semibold text-primary">Vazoul</span> with <span className="text-destructive">♥</span>
            </span>
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground/60">
              <button onClick={() => setFeedbackOpen(true)} className="hover:text-primary transition-colors">Send Feedback</button>
              <span>·</span>
              <a href="/legal/terms" className="hover:text-primary transition-colors">Terms</a>
              <span>·</span>
              <a href="/legal/privacy" className="hover:text-primary transition-colors">Privacy</a>
              <span>·</span>
              <a href="/legal/cookies" className="hover:text-primary transition-colors">Cookies</a>
              <span>·</span>
              <a href="/legal/acceptable_use" className="hover:text-primary transition-colors">Acceptable Use</a>
            </div>
          </div>
        </footer>
      </AuthProvider>
    </BrowserRouter>
  );
}

const App = () => (
  <ErrorBoundary fallbackMessage="Vazoul encountered an unexpected error">
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AppContent />
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
